import { Injectable } from '@angular/core';
import { asapScheduler, BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map, observeOn } from 'rxjs/operators';

/**
 * Centralized HTTP loading state. Used by LoadingInterceptor to show/hide
 * the global spinner for HttpClient requests. Counter ensures parallel
 * requests keep the loader visible until all complete.
 */
@Injectable({
  providedIn: 'root',
})
export class LoadingService {
  private readonly count = new BehaviorSubject<number>(0);
  private counter = 0;

  /**
   * Observable of current loading state (true when any request is in flight).
   *
   * The emission is deferred to a microtask (asapScheduler) on purpose. Components that start a
   * request from ngOnInit - FormPdfViewerComponent awaits checkEmployeeStatus() there - make the
   * interceptor call show() while Angular is still inside the change-detection pass that already
   * checked <app-custom-spinner>. Emitting synchronously flipped the spinner's @if from false to
   * true mid-pass and threw NG0100 ExpressionChangedAfterItHasBeenCheckedError (reported as the
   * @if branch index changing from -1 to 0). Deferring by a microtask lands the change in a fresh
   * turn with no perceptible delay. distinctUntilChanged() also stops parallel requests from
   * re-emitting the same boolean on every increment.
   */
  readonly isLoading$: Observable<boolean> = this.count.pipe(
    map((n) => n > 0),
    distinctUntilChanged(),
    observeOn(asapScheduler)
  );

  show(): void {
    this.counter = Math.max(0, this.counter) + 1;
    this.count.next(this.counter);
  }

  hide(): void {
    this.counter = Math.max(0, this.counter - 1);
    this.count.next(this.counter);
  }
}
